import { useEffect, useMemo } from "react";
import { Bold } from "@tiptap/extension-bold";
import { Document } from "@tiptap/extension-document";
import { HardBreak } from "@tiptap/extension-hard-break";
import { History } from "@tiptap/extension-history";
import { Italic } from "@tiptap/extension-italic";
import { Paragraph } from "@tiptap/extension-paragraph";
import { Placeholder } from "@tiptap/extension-placeholder";
import { Text } from "@tiptap/extension-text";
import { Underline } from "@tiptap/extension-underline";
import type { EditorEvents } from "@tiptap/react";
import { EditorContent, useEditor } from "@tiptap/react";
import { useBuilderStore } from "@/react/components/shared/BuilderUI";
import { CustomizedLink } from "@/react/components/shared/uikit/TipTap/Extensions/CustomExtensions/CustomizedLink";
import { type TipTapContent } from "@circle-react-shared/BuilderUI/DefaultBlocks/utils/textTypes";
import { BlockEditorContext } from "@circle-react-uikit/TipTapBlockEditor";
import { EntitiesExtension } from "./Extensions/Entities";
import { MentionsExtension } from "./Extensions/Mentions";
import { useSgidContext } from "./SgIdProvider";
import { type LinkStyles, getLinkStyles } from "./Utils/getLinkStyles";
import { VisualEmailMenuBar } from "./VisualEmailMenuBar";

interface GetVisualEmailTiptapExtensionsProps {
  isIframePreview?: boolean;
  isPreviewMode?: boolean;
  linkStyles?: LinkStyles;
  onLocalSgidToObjectMapChange?: ({
    sgid,
    object,
  }: {
    sgid: string;
    object: unknown;
  }) => void;
  placeholder?: string;
  sgIdToObjectMap?: Record<string, unknown>;
}

export const getVisualEmailTiptapExtensions = ({
  linkStyles,
  placeholder,
  isPreviewMode = false,
  sgIdToObjectMap,
  onLocalSgidToObjectMapChange,
}: GetVisualEmailTiptapExtensionsProps) => [
  Document,
  Paragraph,
  Text,
  Bold,
  Italic,
  Underline,
  HardBreak.extend({
    addKeyboardShortcuts() {
      return {
        Enter: () => {
          const editor = this.editor;
          editor.commands.setHardBreak();
          return true;
        },
      };
    },
  }),
  CustomizedLink.configure({
    openOnClick: false,
    autolink: false,
    HTMLAttributes: {
      class: "wb-text-link wb-underline",
      style: getLinkStyles(linkStyles),
    },
  }),
  History,
  ...(placeholder ? [Placeholder.configure({ placeholder })] : []),
  MentionsExtension.configure({
    sgIdToObjectMap,
    onLocalSgidToObjectMapChange,
    isPreviewMode,
  }),
  EntitiesExtension.configure({
    sgIdToObjectMap,
    onLocalSgidToObjectMapChange,
    isPreviewMode,
  }),
];

export interface VisualEmailTiptapEditorProps {
  onBlur?: (props: EditorEvents["blur"]) => void;
  onChange?: (content: TipTapContent) => void;
  onDoubleClick?: () => void;
  placeholder?: string;
  readOnly?: boolean;
  shouldFocusWhenReady?: boolean;
  shouldShowMenuBar?: boolean;
  value?: string | TipTapContent;
}

export const VisualEmailTiptapEditor = ({
  value,
  onChange,
  onBlur,
  placeholder = "Enter text...",
  readOnly = false,
  shouldShowMenuBar = false,
  shouldFocusWhenReady = false,
}: VisualEmailTiptapEditorProps) => {
  const { localSgidToObjectMap, onLocalSgidToObjectMapChange } =
    useSgidContext();
  const isIframePreview = useBuilderStore(state => state.isIframePreview);

  const extensions = useMemo(
    () =>
      getVisualEmailTiptapExtensions({
        placeholder,
        sgIdToObjectMap: localSgidToObjectMap,
        onLocalSgidToObjectMapChange,
        isIframePreview,
      }),
    [
      placeholder,
      onLocalSgidToObjectMapChange,
      localSgidToObjectMap,
      isIframePreview,
    ],
  );

  const editor = useEditor(
    {
      extensions,
      content: value,
      editable: !readOnly,
      onUpdate: ({ editor }) => {
        if (!readOnly && onChange) {
          const json = editor.getJSON() as TipTapContent;
          onChange(json);
        }
      },
      onBlur,
      editorProps: {
        attributes: {
          class: "focus:outline-none",
          style:
            "max-width: 100% !important; overflow: hidden !important; box-sizing: border-box !important; word-wrap: break-word !important; word-break: break-word !important;",
        },
      },
    },
    [readOnly],
  );

  // Only update editor content when it's first created or when value changes externally
  // (not from typing). This prevents cursor reset on every keystroke.
  useEffect(() => {
    if (editor && !editor.isFocused) {
      editor.commands.setContent(value);
      if (shouldFocusWhenReady) {
        editor.commands.selectAll();
        editor.commands.focus();
      }
    }
  }, [editor, value, shouldFocusWhenReady]);

  if (!shouldShowMenuBar) {
    return (
      <BlockEditorContext.Provider
        value={{
          localSgidToObjectMap,
          addToLocalSgidToObjectMap: onLocalSgidToObjectMapChange,
        }}
      >
        <EditorContent editor={editor} data-testid="builder-tiptap-editor" />
      </BlockEditorContext.Provider>
    );
  }

  return (
    <BlockEditorContext.Provider
      value={{
        localSgidToObjectMap,
        addToLocalSgidToObjectMap: onLocalSgidToObjectMapChange,
      }}
    >
      <div
        className="min-h-[100px] max-w-full overflow-hidden rounded-lg border p-3 pt-0"
        style={{ maxWidth: "100%", overflow: "hidden" }}
      >
        <VisualEmailMenuBar editor={editor} />
        <EditorContent
          editor={editor}
          data-testid="builder-tiptap-editor"
          style={{ maxWidth: "100%", overflow: "hidden" }}
        />
      </div>
    </BlockEditorContext.Provider>
  );
};
